/**
 * Universal Intake — the client's half of the format contract.
 *
 * THE SERVER IS THE SOURCE OF TRUTH: services/intake_format_registry.py.
 * This file is its client mirror, and tests/gate/test_intake_client_registry_lockstep.py
 * binds the two so a drift fails CI instead of reaching a user.
 *
 * WHY A CONSTANT AND NOT A WILDCARD
 * ---------------------------------
 * The registry exists because of a real incident: on 2026-08-12 a user asked whether he
 * could send his Strava history, Cedar invited an `.xlsx`, and the picker then refused
 * the file. The registry's own docstring names the shape of that bug —
 *
 *     "the web picker's accept= attribute, which OFFERS .doc/.docx/.txt/.fit/.gpx
 *      that [validation] then rejects"
 *
 * — and `client_accept_attribute()` was written to prevent it. It was never called by
 * either client, so the bug survived in a different costume: the accept attribute read
 * `image/*,video/*,...`, and those two WILDCARDS offer every format the browser knows,
 * including `.heic`. HEIC is the iPhone's default photo format, and it is UNSUPPORTED in
 * the registry — so an iPhone user picking a photo on the web was offered the file, then
 * told "I can't read .heic yet. Right now I can read photos..." in the same breath.
 *
 * A wildcard cannot be kept in lockstep with an allow-list, because it means "whatever
 * this browser happens to support." Enumerate, or the contradiction returns.
 *
 * DO NOT hand-edit the values below. Change services/intake_format_registry.py and copy
 * what it renders:
 *
 *     python3 -c "import sys; sys.path.insert(0,'.'); \
 *       from services.intake_format_registry import client_accept_attribute, \
 *       accepted_extensions; print(client_accept_attribute())"
 */

/**
 * The `accept=` value for the web file input.
 *
 * Verbatim `services.intake_format_registry.client_accept_attribute()`. Explicit MIME
 * types plus explicit extensions, never a wildcard — the gate rejects `/*`.
 */
export const INTAKE_ACCEPT_ATTRIBUTE =
  'image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp,image/gif,.gif,image/bmp,.bmp,' +
  'image/heic,.heic,image/heif,.heif,application/pdf,.pdf,application/vnd.garmin.tcx+xml,' +
  '.tcx,text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  '.xlsx,application/vnd.ms-excel.sheet.macroEnabled.12,.xlsm,application/zip,' +
  'application/x-zip-compressed,.zip,video/mp4,.mp4,video/quicktime,.mov,video/x-m4v,.m4v,' +
  'video/webm,.webm,video/x-msvideo,.avi';

/**
 * Extensions the client will hand to the server.
 *
 * Verbatim `services.intake_format_registry.accepted_extensions()` — SUPPORTED and
 * DEGRADED, never UNSUPPORTED. Every extension offered by INTAKE_ACCEPT_ATTRIBUTE must
 * appear here, or the picker offers what validation refuses.
 */
export const INTAKE_ALLOWED_EXTENSIONS: readonly string[] = [
  '.avi', '.bmp', '.csv', '.gif', '.heic', '.heif', '.jpeg', '.jpg', '.m4v', '.mov',
  '.mp4', '.pdf', '.png', '.tcx', '.webm', '.webp', '.xlsm', '.xlsx', '.zip',
];

/** Fallback cap, mirroring the registry's DEFAULT_MAX_MB. */
export const INTAKE_DEFAULT_MAX_MB = 5;

/**
 * Per-format size caps in MB, keyed WITH the leading dot.
 *
 * Mirrors each registry entry's `max_mb`. Only formats that override the default appear.
 * A client cap BELOW the server's is a false refusal the user cannot appeal — which is
 * how a phone video was refused at about three seconds while the prompt invited "a short
 * video of a movement", and how a 40MB Apple Health export was refused at 25.
 */
export const INTAKE_CAP_MB_BY_EXTENSION: Readonly<Record<string, number>> = {
  '.csv': 25, '.xlsx': 25, '.xlsm': 25, '.zip': 50,
  '.mp4': 25, '.mov': 25, '.m4v': 25, '.webm': 25, '.avi': 25,
};

/**
 * The cap for one file, in MB.
 *
 * Accepts an extension with or without the leading dot, and is case-insensitive — the
 * two clients derive `ext` differently (web keeps the dot, native strips it), and that
 * difference has no business being a behavioural one.
 */
export function intakeCapMb(extension: string | null | undefined): number {
  if (!extension) return INTAKE_DEFAULT_MAX_MB;
  const normalized = (extension.startsWith('.') ? extension : `.${extension}`).toLowerCase();
  return INTAKE_CAP_MB_BY_EXTENSION[normalized] ?? INTAKE_DEFAULT_MAX_MB;
}

/** True when the client will accept this filename. Case-insensitive. */
export function isIntakeExtensionAllowed(extension: string | null | undefined): boolean {
  if (!extension) return false;
  const normalized = (extension.startsWith('.') ? extension : `.${extension}`).toLowerCase();
  return INTAKE_ALLOWED_EXTENSIONS.includes(normalized);
}


/**
 * The sentence Cedar uses when it refuses a file.
 *
 * Hand-written in TWO clients until 2026-08-20, and they already disagreed with the
 * server about what Cedar can read — the web copy still promised only "text out of PDFs"
 * long after pages began being rendered and read by sight.
 *
 * This is the FALLBACK. `fetchIntakeFormats()` below refreshes it from
 * `GET /api/intake/formats`, which serves the registry itself. A client that cannot
 * reach the endpoint behaves exactly as it does today.
 */
export const INTAKE_ACCEPTED_SUMMARY =
  'Right now I can read photos and screenshots, documents and plans (.pdf), workout files (.tcx), training history exports (.csv, .xlsx), Apple Health exports (.zip), and a short video of a movement.';

export interface IntakeFormatsResponse {
  accepted: string[];
  accept_attribute: string;
  summary: string;
  limitations: Record<string, string>;
  caps_mb: Record<string, number>;
  default_cap_mb: number;
}

/**
 * Ask the server what it accepts. Returns null on ANY failure — the caller keeps the
 * constants above, so this can only ever improve the copy, never block an upload.
 */
export async function fetchIntakeFormats(): Promise<IntakeFormatsResponse | null> {
  try {
    const res = await fetch('/api/intake/formats');
    if (!res.ok) return null;
    return (await res.json()) as IntakeFormatsResponse;
  } catch {
    return null;
  }
}
